const KITCHEN_ROLE = 'Kitchen';
const WAITER_ROLE = 'Waiter';
const CORE_ROLES = [KITCHEN_ROLE, WAITER_ROLE];

export function hasSegmentedRoles(salaryExpenses) {
  if (!salaryExpenses || typeof salaryExpenses !== 'object') return false;
  return KITCHEN_ROLE in salaryExpenses && WAITER_ROLE in salaryExpenses;
}

export function processSalaryExpenses(salaryExpenses) {
  if (!salaryExpenses) return { total: 0, kitchen: 0, waiter: 0, hasSegmentation: false };
  
  if (typeof salaryExpenses === 'number') {
    return { total: salaryExpenses, kitchen: 0, waiter: 0, hasSegmentation: false };
  }

  const hasSegmentation = hasSegmentedRoles(salaryExpenses);
  
  if (!hasSegmentation) {
    const total = Object.values(salaryExpenses).reduce((sum, val) => sum + (val || 0), 0);
    return { total, kitchen: 0, waiter: 0, hasSegmentation: false };
  }

  let kitchenExpense = salaryExpenses[KITCHEN_ROLE] || 0;
  let waiterExpense = salaryExpenses[WAITER_ROLE] || 0;
  
  let otherExpenses = 0;
  for (const [role, expense] of Object.entries(salaryExpenses)) {
    if (!CORE_ROLES.includes(role)) {
      otherExpenses += expense || 0;
    }
  }
  
  const splitOther = otherExpenses / 2;
  kitchenExpense += splitOther;
  waiterExpense += splitOther;
  
  const total = kitchenExpense + waiterExpense;
  
  return {
    total,
    kitchen: kitchenExpense,
    waiter: waiterExpense,
    hasSegmentation: true
  };
}

export function getTotalExpense(salaryExpenses) {
  return processSalaryExpenses(salaryExpenses).total;
}

export function aggregateWeeklyExpenses(weekExpensesData) {
  if (!weekExpensesData || typeof weekExpensesData !== 'object') {
    return { total: 0, kitchen: 0, waiter: 0, hasSegmentation: false };
  }

  let hasAnySegmentation = false;
  let totalKitchen = 0;
  let totalWaiter = 0;
  let total = 0;

  for (const [key, dayExpenses] of Object.entries(weekExpensesData)) {
    if (key === 'index') continue;
    
    const processed = processSalaryExpenses(dayExpenses);
    total += processed.total;
    
    if (processed.hasSegmentation) {
      hasAnySegmentation = true;
      totalKitchen += processed.kitchen;
      totalWaiter += processed.waiter;
    }
  }

  return {
    total,
    kitchen: totalKitchen,
    waiter: totalWaiter,
    hasSegmentation: hasAnySegmentation
  };
}

export function calculateSegmentedSalaryPercentage(revenue, salaryExpenses) {
  const processed = processSalaryExpenses(salaryExpenses);
  
  if (!revenue || revenue === 0) {
    return {
      total: 0,
      kitchen: 0,
      waiter: 0,
      hasSegmentation: processed.hasSegmentation
    };
  }

  return {
    total: (processed.total / revenue) * 100,
    kitchen: processed.hasSegmentation ? (processed.kitchen / revenue) * 100 : 0,
    waiter: processed.hasSegmentation ? (processed.waiter / revenue) * 100 : 0,
    hasSegmentation: processed.hasSegmentation
  };
}

export function calculateWeeklySegmentedSalaryPercentage(totalRevenue, weekExpensesData) {
  const aggregated = aggregateWeeklyExpenses(weekExpensesData);
  
  if (!totalRevenue || totalRevenue === 0) {
    return {
      total: 0,
      kitchen: 0,
      waiter: 0,
      hasSegmentation: aggregated.hasSegmentation
    };
  }

  return {
    total: (aggregated.total / totalRevenue) * 100,
    kitchen: aggregated.hasSegmentation ? (aggregated.kitchen / totalRevenue) * 100 : 0,
    waiter: aggregated.hasSegmentation ? (aggregated.waiter / totalRevenue) * 100 : 0,
    hasSegmentation: aggregated.hasSegmentation
  };
}

export const ROLE_LABELS = {
  kitchen: { da: 'Køkken', en: 'Kitchen' },
  waiter: { da: 'Tjenere', en: 'Waiter' }
};

